import {useApolloClient} from '@apollo/client';
import {usePlaidUpdatesAvailableQuery} from '@transactions/graphql';
import {DateTime} from 'luxon';
import * as React from 'react';

/**
 * What a finished sync refreshes, for SyncModal and a background sync alike:
 * everything a sync's balance readings, transactions and investment activity
 * feed (only mounted queries refetch, so off-page names are free).
 */
export const SYNC_REFRESHED_QUERIES = [
  'RecentSyncs',
  'AllRawTransactions',
  'InboxBacklogSummary',
  'AllInvestmentAccounts',
  'AllInvestmentAccountsPerformance',
  'PortfolioPerformanceSummary',
  'PortfolioXIRR',
  'ConsolidatedPortfolioHistory',
  'PortfolioHistoryByAccount',
  'InvestmentReturnsMatrix',
  'InvestmentPeriods',
  'InvestmentHoldings',
  'InvestmentGaps',
  'InvestmentActivity',
  // Net worth and its split refresh together, so the split's rest is
  // never one side's new figures against the other's old.
  'NetWorth',
  'NetWorthInvestmentSplit',
];

// How stale the indicator may be before it is worth re-checking. The dot is a
// passive nudge on a manual action, so a few minutes of lag costs nothing —
// and this doubles as the guard that keeps flipping between tabs from turning
// into one request per flip.
const FRESH_FOR_MS = 5 * 60 * 1000;

// How often to re-evaluate staleness while visible. Local comparison only; a
// tick that finds fresh data makes no request.
const CHECK_INTERVAL_MS = 60 * 1000;

const nowMs = (): number => DateTime.now().toMillis();

/**
 * Whether a Plaid webhook has signaled updates that haven't been synced yet.
 *
 * Re-checks when the tab becomes visible, and at most once per
 * `FRESH_FOR_MS` while it stays visible. Hidden tabs make no requests at
 * all: Apollo's `pollInterval` fires regardless of visibility, so a dozen
 * background tabs each cost a request a minute to refresh a dot nobody can
 * see. Syncing clears it — via the SyncModal's refetch on completion, or
 * server-side when the hourly webhook-gated cron sync consumes the signal.
 */
export const useSyncUpdatesAvailable = (): boolean => {
  const apolloClient = useApolloClient();
  const {data, refetch} = usePlaidUpdatesAvailableQuery();
  const available = data?.plaidUpdatesAvailable ?? false;

  // Marker for "a sync has finished since this tab last looked": the newest
  // *completed* session. Still-running sessions don't count — a sync in
  // flight hasn't finished writing what the list queries read — and a
  // session orphaned as 'running' by a crash must not mask later
  // completions (the query fetches a few sessions for exactly that reason).
  // undefined = no response yet; null = no completed sync exists.
  const latestCompletedSyncId =
    data == null
      ? undefined
      : (data.recentSyncs.find((s) => s.completedAt != null)?._id ?? null);

  // When the marker advances, some sync — the modal's, or the hourly
  // background cron's — finished and may have changed what the list queries
  // show. (After a modal sync this dedupes into the modal's own refetch.)
  // Keyed on the marker rather than an updatesAvailable true -> false flip
  // so a background sync whose pending state this tab never observed —
  // webhook and cron both landing between two availability checks — still
  // refreshes an open tab.
  const prevSyncIdRef = React.useRef<string | null | undefined>(undefined);
  React.useEffect(() => {
    if (latestCompletedSyncId === undefined) {
      return;
    }
    const prev = prevSyncIdRef.current;
    prevSyncIdRef.current = latestCompletedSyncId;
    if (prev === undefined || prev === latestCompletedSyncId) {
      return;
    }
    // A background sync writes balance readings and investment
    // transactions, so the whole set refreshes as a group.
    void apolloClient.refetchQueries({include: SYNC_REFRESHED_QUERIES});
  }, [latestCompletedSyncId, apolloClient]);

  // Mounting issues the initial fetch, so the clock starts now.
  const lastFetchedAtRef = React.useRef(nowMs());

  React.useEffect(() => {
    const refetchIfStale = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      if (nowMs() - lastFetchedAtRef.current < FRESH_FOR_MS) {
        return;
      }
      lastFetchedAtRef.current = nowMs();
      // A failed check just leaves the dot as it was; the next tick retries.
      refetch().catch(() => undefined);
    };

    const timer = window.setInterval(refetchIfStale, CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', refetchIfStale);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', refetchIfStale);
    };
  }, [refetch]);

  return available;
};
